import { ListItemText, Autocomplete, TextField, debounce, Grid } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import AlertSnackbar from "../alertSnackbar";
import { getSelectedTaxonomyIds } from "../../helpers/taxonomyHelpers";
import { usePostQuery } from "../../hooks/usePostQuery";
import { DimensionDetailedView } from "./dimensionDetailedView";
import { DimensionValueReference, DimensionValueReferenceAsResult, PageSelector } from "../../api/customObjectsTypes";

export const DimensionLabel = ({
    label,
    selectedValue,
    setSelectedValue,
    value,
    onValueChange,
    preDefinedOptions,
    placeholder,
    disabled,
    includeCustom = true,
    showDetailed = true,
    page = "normal",
    alternateUrl,
    dimensionName,
    taxonomyId,
}: {
    includeCustom?: boolean | null;
    label: string | undefined;
    preDefinedOptions?: any[];
    selectedValue: any | null;
    setSelectedValue: (value: any | null) => void;
    value?: DimensionValueReference;
    onValueChange?: (event: any) => void;
    placeholder?: string;
    disabled?: boolean;
    showDetailed?: boolean;
    page?: PageSelector;
    alternateUrl?: "alternateDimension" | "alternateParentMember" | "alternateElement" | "alternateParentMemberDimension";
    dimensionName?: string;
    taxonomyId?: string;
}) => {
    const [query, setQuery] = useState("");
    const [optionsState, setOptionsState] = useState<DimensionValueReference[]>(Array.isArray(preDefinedOptions) ? preDefinedOptions : []);
    const [cacheBuster] = useState(crypto.randomUUID());
    const [abortSignal, setAbortSignal] = useState(true);
    const [req, setReq] = useState(() => {
        const initialReq: {
            searchString: string;
            pageCount: number;
            taxonomyId?: string;
            taxonomyIds?: string[];
        } = {
            searchString: query,
            pageCount: 20,
        };
        if (taxonomyId) {
            initialReq.taxonomyId = taxonomyId;
        } else {
            initialReq.taxonomyIds = getSelectedTaxonomyIds();
        }
        return initialReq;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleQueryChange = useCallback(debounce((newQuery: string) => {
        const updatedReq: {
            searchString: string;
            pageCount: number;
            taxonomyId?: string;
            taxonomyIds?: string[];
        } = {
            searchString: newQuery,
            pageCount: 20,
        };

        if (taxonomyId && !dimensionName) {
            updatedReq.taxonomyId = taxonomyId;
        } else {
            updatedReq.taxonomyIds = taxonomyId ? [taxonomyId] : getSelectedTaxonomyIds();
        }

        setReq(updatedReq);
        setAbortSignal(false);
    }, 500), [setReq, setAbortSignal]);

    useEffect(() => {
        if (query.length > 0) {
            handleQueryChange(query);
        }
    }, [query, handleQueryChange]);

    const urlPicker = () => {
        if (alternateUrl === "alternateParentMember") {
            return `/api/custom-objects/dimensions/${dimensionName}/members/search-by-name?${includeCustom !== null ? `includeCustom=${includeCustom}&cb=${cacheBuster}` : ''}`;
        } else if (alternateUrl === "alternateParentMemberDimension") {
            return `/api/custom-objects/dimensions/${dimensionName}/members/search-by-name?${includeCustom !== null ? `includeCustom=${includeCustom}&cb=${cacheBuster}` : ''}`;
        } else if (alternateUrl === "alternateElement") {
            return `/api/custom-objects/dimension-value-references/dimensions/${dimensionName}/members/search-by-name?cb=${cacheBuster}${includeCustom !== null ? `&includeCustom=${includeCustom}` : ''}`;
        } else {
            return `/api/custom-objects/${alternateUrl === "alternateDimension" ? 'dimension-references' : 'dimensions'}/search?cb=${cacheBuster}${includeCustom !== null ? `&includeCustom=${includeCustom}` : ''}`;
        }
    };

    const { data, loading, error } = usePostQuery<DimensionValueReferenceAsResult>(
        urlPicker(),
        req,
        abortSignal
    );

    useEffect(() => {
        if (data) {
            if (Array.isArray(data.result)) {
                setOptionsState(data.result as DimensionValueReference[]);
            } else if (Array.isArray(data)) {
                setOptionsState(data as DimensionValueReference[]);
            } else {
                setOptionsState([]);
            }
        }
    }, [data]);

    useEffect(() => {
        if (value === null) {
            setSelectedValue(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const sortedOptions = [
        ...optionsState.filter(option => option.isCustom === false),
        ...optionsState.filter(option => option.isCustom !== false)
    ];

    return (
        <Grid container spacing={0} sx={{ mb: 2 }} maxWidth={"false"}>
            {error &&
                <AlertSnackbar
                    alertMessage={{ message: error.message, severity: "error" }}
                    updateAlertStatus={() => { }}
                />
            }
            <Grid size={2}>
                <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
            </Grid>
            <Grid size={10}>
                <Autocomplete
                    disabled={disabled}
                    sx={{
                        "& .MuiAutocomplete-inputRoot.Mui-disabled": {
                            cursor: "not-allowed",
                        },
                    }}
                    forcePopupIcon={false}
                    options={sortedOptions}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => option?.name || ""}
                    renderOption={(props, option, state) => {
                        const { key, ...restProps } = props;
                        const isEven = state.index % 2 === 0;
                        return (
                            <li key={state.index} {...restProps}
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    listStyleType: "none",
                                }}
                            >
                                {option &&
                                    <DimensionDetailedView
                                        element={option}
                                        isEven={isEven}
                                        page={page}
                                    />}
                            </li>
                        );
                    }}
                    loading={loading}
                    value={selectedValue || null}
                    onChange={(_, newValue) => {
                        setSelectedValue(newValue || null);
                        if (onValueChange && newValue) {
                            onValueChange(newValue);
                        }
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                        if (reason === "clear") {
                            setSelectedValue(null);
                            if (onValueChange) {
                                onValueChange(null);
                            }
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={placeholder || ""}
                            sx={{ width: "100%" }}
                            variant="standard"
                            disabled={disabled}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    )}
                />
                {selectedValue && showDetailed &&
                    <DimensionDetailedView
                        isOption={selectedValue}
                        element={selectedValue}
                        page={page}
                        isEven={optionsState.indexOf(selectedValue) % 2 === 0}
                    />}
            </Grid>
        </Grid>
    );
};

export default DimensionLabel;